import { useEffect, useRef, useState } from 'react';
import {
  describeResult,
  loadResult,
  recoverInterruptedRun,
  runGraphicsCalibration,
  viewerMemoryMb,
  type CalibrationProgress,
  type CalibrationResult,
} from '../viewer/gpuCalibration';
import { Button } from './ui/Button';
import { confirm } from './ui/Confirm';
import { Notice } from './ui/Controls';

/**
 * Settings → Graphics calibration. Runs viewer/gpuCalibration full screen and shows
 * what it measured. Results belong to this computer and browser, not the
 * account, so they live in localStorage.
 */

const WARNING = (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
    <span>Pushes this computer’s graphics to their limit for about 20 seconds.</span>
    <span style={{ padding: '9px 12px', borderRadius: 8, background: '#fff8e6', border: '1px solid #f3dea3', color: '#5c4400' }}>
      <strong>The screen may flicker or go black, and the browser may close.</strong> That is expected: it is how the
      limit is found.
    </span>
    <span>
      <strong style={{ color: 'var(--tr-ink)' }}>Save your work in other tabs first.</strong> If the browser closes, come
      back to this page. The result is kept.
    </span>
  </div>
);

export function GraphicsCalibrationSection() {
  // Read once: a marker left by a test the browser did not survive becomes its
  // result here.
  const [recovered] = useState(() => recoverInterruptedRun());
  const [result, setResult] = useState<CalibrationResult | null>(() => recovered ?? loadResult());
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const start = async () => {
    setError(null);
    if (!(await confirm({ title: 'Run graphics calibration?', body: WARNING, action: 'Start calibration' }))) return;
    setRunning(true);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 560 }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>Graphics calibration</span>
          <span style={{ fontSize: 13, color: '#6b6b70', lineHeight: 1.5 }}>
            Optimizes the image viewer’s performance on this computer.
          </span>
        </div>
        <Button variant="outlined" size={32} onClick={start} disabled={running}>
          {result ? 'Calibrate again' : 'Calibrate'}
        </Button>
      </div>
      {recovered ? (
        <Notice>
          The browser closed during the last calibration ({recovered.stoppedAt}). That is where this computer’s limit is, and the
          result below counts it.
        </Notice>
      ) : null}
      {result?.ending === 'gpu-lost' && !recovered ? (
        <Notice>The graphics reset during calibration ({result.stoppedAt}). That is this computer’s limit.</Notice>
      ) : null}
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {result ? (
        <ResultCard
          r={result}
          copied={copied}
          onCopy={async () => {
            try {
              await navigator.clipboard.writeText(describeResult(result));
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            } catch {
              setError('Could not copy to the clipboard.');
            }
          }}
        />
      ) : null}
      {running ? (
        <TestOverlay
          onDone={(r, err) => {
            setRunning(false);
            if (r) setResult(r);
            if (err) setError(err);
          }}
        />
      ) : null}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '12px 14px', border: '1px solid #ebebea', borderRadius: 10 }}>
      <span style={{ fontSize: 12, color: '#6b6b70' }}>{label}</span>
      <span style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-.01em' }}>{value}</span>
      {sub ? <span style={{ fontSize: 12, color: '#8a8a8f' }}>{sub}</span> : null}
    </div>
  );
}

function ResultCard({ r, copied, onCopy }: { r: CalibrationResult; copied: boolean; onCopy: () => void }) {
  const uploadsMeasured = r.burstPerFrame > 0;
  const mbPerSec = Math.round((r.uploadTilesPerSec * 256 * 1024) / (1024 * 1024));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        <Stat label="Graphics score" value={`${r.score}/100`} sub={`Viewer memory: ${viewerMemoryMb(r)} MB`} />
        <Stat
          label="Tile memory, smooth"
          value={r.memoryMb > 0 ? `${r.memoryMb} MB` : 'Under 64 MB'}
          sub={r.frameP95Ms != null ? `Frames ${r.frameP95Ms} ms (p95)` : undefined}
        />
        <Stat
          label="Upload rate"
          value={uploadsMeasured ? `${r.uploadTilesPerSec.toLocaleString()} tiles/s` : 'Not reached'}
          sub={uploadsMeasured ? `${mbPerSec} MB/s, up to ${r.burstPerFrame} a frame` : undefined}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: '#8a8a8f', lineHeight: 1.5 }}>
          {r.gpu ?? 'Unknown graphics'} · {new Date(r.at).toLocaleString()}
          {r.stoppedAt ? ` · Limit reached at ${r.stoppedAt}` : ' · Passed every step'}
        </span>
        <Button variant="quiet" size={28} onClick={onCopy}>
          {copied ? 'Copied' : 'Copy results'}
        </Button>
      </div>
    </div>
  );
}

function TestOverlay({ onDone }: { onDone: (r: CalibrationResult | null, error: string | null) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [progress, setProgress] = useState<CalibrationProgress | null>(null);
  const abort = useRef(new AbortController());
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Full screen at device pixels, as big as the viewer draws.
    canvas.width = Math.round(window.innerWidth * window.devicePixelRatio);
    canvas.height = Math.round(window.innerHeight * window.devicePixelRatio);
    const ctl = abort.current;
    runGraphicsCalibration(canvas, setProgress, ctl.signal).then(
      (r) => doneRef.current(r, null),
      (err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') doneRef.current(null, null);
        else doneRef.current(null, err instanceof Error ? err.message : 'Calibration could not run.');
      },
    );
    return () => ctl.abort();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && abort.current.abort();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: '#1a1a1a' }}>
      <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
      <div
        style={{
          position: 'absolute',
          top: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '10px 12px 10px 16px',
          borderRadius: 10,
          background: 'rgba(255,255,255,.96)',
          boxShadow: '0 6px 24px rgba(0,0,0,.25)',
          minWidth: 320,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>
            {progress ? `${progress.stage === 'memory' ? '1 of 2' : '2 of 2'} · ${progress.label}` : 'Starting…'}
          </span>
          <div style={{ height: 4, borderRadius: 2, background: '#ebebea', overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: `${Math.round(((progress?.stage === 'uploads' ? 1 : 0) + (progress?.fraction ?? 0)) * 50)}%`,
                background: '#1a1a1a',
                transition: 'width .2s',
              }}
            />
          </div>
        </div>
        <Button variant="outlined" size={28} onClick={() => abort.current.abort()}>
          Stop
        </Button>
      </div>
    </div>
  );
}
